import { EventEmitter } from '../../core/Event';
import type { Random } from '../../core/Random';
import type { CrashEvent } from './CrashEvents';
import type { CrashConfig, CrashStatus } from './CrashTypes';

const DEFAULT_GROWTH_RATE_PER_SEC = 0.5;
const DEFAULT_HOUSE_EDGE = 0.01;

/**
 * Authoritative state for a single Crash round + the player's running
 * balance. The model exposes:
 * <ul>
 *   <li>{@link startRound} — pre-rolls the crash point, sets status to
 *       {@code 'rising'}, debits the bet.</li>
 *   <li>{@link tick} — host calls per-frame to advance the multiplier
 *       along the growth curve. Emits {@code crashed} when the curve
 *       hits the pre-rolled crash point.</li>
 *   <li>{@link cashOut} — locks in the current multiplier, credits
 *       {@code bet × multiplier}, ends the round.</li>
 * </ul>
 *
 * <p>The crash point is rolled at round start using the canonical
 * provably-fair Crash distribution (Pareto-shaped). The exact value
 * stays private until the round ends so the host UI can't accidentally
 * leak it to the player.
 */
export class CrashModel {
  public readonly events = new EventEmitter<CrashEvent>();
  public readonly bet: number;
  private readonly growthRatePerSec: number;
  private readonly houseEdge: number;
  private readonly random: Random;
  private _balance: number;
  private _status: CrashStatus = 'idle';
  private _multiplier = 1;
  private _crashPoint = 1;
  private _cashedAt: number | null = null;

  constructor(config: CrashConfig, random: Random) {
    this._balance = config.startingBalance;
    this.bet = config.bet;
    this.growthRatePerSec = config.growthRatePerSec ?? DEFAULT_GROWTH_RATE_PER_SEC;
    this.houseEdge = config.houseEdge ?? DEFAULT_HOUSE_EDGE;
    this.random = random;
  }

  get balance(): number {
    return this._balance;
  }

  get status(): CrashStatus {
    return this._status;
  }

  get multiplier(): number {
    return this._multiplier;
  }

  /** Multiplier the player cashed at this round, or null if they didn't
   * cash out / there's no active result. */
  get cashedAt(): number | null {
    return this._cashedAt;
  }

  /** Crash point of the most recent round — only valid after
   * {@code crashed} has fired. Hosts use this for the post-round panel;
   * the value is meaningless mid-round. */
  get lastCrashPoint(): number {
    return this._crashPoint;
  }

  /** Begin a new round. Debits the bet, rolls the crash point, sets
   * status to {@code 'rising'}. Returns false (no-op) if a round is
   * already in flight or the player can't afford the bet. */
  startRound(): boolean {
    if (this._status === 'rising') return false;
    if (this._balance < this.bet) return false;

    const balBefore = this._balance;
    this._balance -= this.bet;
    this.events.emit({
      kind: 'balance-changed',
      before: balBefore,
      after: this._balance,
      reason: 'round-bet',
    });

    this._crashPoint = rollCrashPoint(this.random, this.houseEdge);
    this._multiplier = 1;
    this._cashedAt = null;
    this._status = 'rising';
    this.events.emit({ kind: 'round-started', bet: this.bet });
    this.events.emit({ kind: 'multiplier-changed', multiplier: 1 });
    return true;
  }

  /** Advance the multiplier by {@code deltaMs} milliseconds along the
   * exponential growth curve. Emits {@code multiplier-changed} on every
   * tick; emits {@code crashed} (then {@code round-ready}) when the
   * multiplier reaches the pre-rolled crash point. No-op when not in
   * the {@code 'rising'} state. */
  tick(deltaMs: number): void {
    if (this._status !== 'rising') return;
    // Continuous compound growth: m(t) = m0 * (1 + r)^(dt/1000).
    const growthFactor = Math.pow(1 + this.growthRatePerSec, deltaMs / 1000);
    let next = this._multiplier * growthFactor;
    if (next >= this._crashPoint) {
      next = this._crashPoint;
      this._multiplier = next;
      this.events.emit({ kind: 'multiplier-changed', multiplier: next });
      this._status = 'crashed';
      this.events.emit({ kind: 'crashed', crashedAt: this._crashPoint, lostBet: true });
      this.events.emit({ kind: 'round-ready', status: 'crashed' });
      return;
    }
    this._multiplier = next;
    this.events.emit({ kind: 'multiplier-changed', multiplier: next });
  }

  /** Lock in the current multiplier. Pays {@code bet × multiplier}
   * (rounded down). Returns false if not currently rising. */
  cashOut(): boolean {
    if (this._status !== 'rising') return false;
    this._cashedAt = this._multiplier;
    this._status = 'cashed-out';
    const winnings = Math.floor(this.bet * this._cashedAt);
    if (winnings > 0) {
      const balBefore = this._balance;
      this._balance += winnings;
      this.events.emit({
        kind: 'balance-changed',
        before: balBefore,
        after: this._balance,
        reason: 'cash-out',
      });
    }
    this.events.emit({ kind: 'cashed-out', multiplier: this._cashedAt, winnings });
    // The round still officially "crashes" at its pre-rolled point —
    // the host UI uses lastCrashPoint to show the player where it would
    // have ended. We emit round-ready immediately though so the next
    // round can start.
    this.events.emit({ kind: 'round-ready', status: 'cashed-out' });
    return true;
  }

  dispose(): void {
    this.events.clear();
  }
}

function rollCrashPoint(random: Random, houseEdge: number): number {
  if (random.nextFloat() < houseEdge) return 1.0;
  const r = random.nextFloat();
  // Pareto-shaped: 0.99 / (1 - r) for r in [0, 1).
  // The 0.99 factor keeps the cumulative house edge near {@code houseEdge}.
  return 0.99 / (1 - r);
}
